import { oc } from "@orpc/contract";
import { z } from "zod";
import { ExportFileStreamSchema } from "./products.contract";

// ─── Shared schemas ─────────────────────────────────────────────────────────

/**
 * One price-list appearance of a product. A catalog row can have 0..N of these:
 * a product may sit on several price lists (each with its own price/UOM), or on
 * none at all (in which case `prices` is empty but the row still shows).
 */
export const ProductCatalogPriceSchema = z.object({
  priceListId: z.string().uuid().nullable(),
  priceListName: z.string().nullable(),
  /** Unit price as a decimal string (mirrors the DECIMAL column). */
  unitPrice: z.string(),
  uomCode: z.string().nullable(),
  /** ISO currency of the owning price list (e.g. "USD"). */
  currencyCode: z.string().nullable(),
  /** ISO timestamp of the line's last update (drives the Last Updated column
   *  and the same-price-list dedupe — newest line wins). */
  updatedAt: z.string(),
  /** True when this price's list has an ACTIVE exception-overlay version.
   *  `unitPrice` then carries the overlay's (effective) price and
   *  `originalUnitPrice` the synced list's own price. */
  hasActiveException: z.boolean(),
  /** The synced list's own price when `unitPrice` is superseded by an active
   *  exception overlay; null otherwise (or when the overlay doesn't cover
   *  this product and the synced price still applies). */
  originalUnitPrice: z.string().nullable(),
  /** The synced line's own updatedAt when superseded (pairs with
   *  `originalUnitPrice`); null otherwise. */
  originalUpdatedAt: z.string().nullable(),
  /** True when the effective price comes from a CUSTOMER-scoped exception
   *  list (only possible with a single-customer catalog filter). */
  isCustomerException: z.boolean(),
  /** The general (all-customers) exception list's price when a customer
   *  exception supersedes it and the general list also covers the product —
   *  the middle tier of customer → general → ERP; null otherwise. */
  generalExceptionUnitPrice: z.string().nullable(),
  /** Pairs with `generalExceptionUnitPrice`. */
  generalExceptionUpdatedAt: z.string().nullable(),
});

export const ProductCatalogRowSchema = z.object({
  productVariantId: z.string().uuid(),
  productId: z.string().uuid(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  productFamily: z.string().nullable(),
  productGroup: z.string().nullable(),
  productModel: z.string().nullable(),
  /** The variant's active flag (the active/inactive/all filter keys off this). */
  active: z.boolean(),
  /** Price-list appearances, ordered by price-list name. Empty when unpriced. */
  prices: z.array(ProductCatalogPriceSchema),
});

/** Active/inactive/all toggle — keys off `im_product_variants.active`. */
export const ProductCatalogStatusSchema = z
  .enum(["active", "inactive", "all"])
  .default("active");

/**
 * Filters shared by `list` and `exportExcel`. Customer + price-list filters both
 * restrict which products appear AND scope the price columns to the matching
 * price lists (resolved server-side).
 */
export const ProductCatalogFilterSchema = z.object({
  search: z.string().trim().optional(),
  family: z.string().trim().optional(),
  group: z.string().trim().optional(),
  model: z.string().trim().optional(),
  /** Restrict + scope to the price list(s) the selected customers ship-to. */
  customerId: z.array(z.string().uuid()).optional(),
  /** Restrict + scope to the selected price list(s). */
  priceListId: z.array(z.string().uuid()).optional(),
  /** Show only products that are on NO price list (mutually exclusive with the
   *  customer / priceListId filters). */
  noPriceList: z.boolean().optional(),
  status: ProductCatalogStatusSchema,
});

// ─── list ───────────────────────────────────────────────────────────────────

export const ListProductCatalogInputSchema = ProductCatalogFilterSchema.extend({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const ListProductCatalogResponseSchema = z.object({
  rows: z.array(ProductCatalogRowSchema),
  total: z.number().int().min(0),
});

// ─── classifications ─────────────────────────────────────────────────────────

export const ProductCatalogClassificationSchema = z.object({
  family: z.string(),
  group: z.string(),
  model: z.string(),
});

export const ProductCatalogClassificationsResponseSchema = z.object({
  classifications: z.array(ProductCatalogClassificationSchema),
});

// ─── totals ───────────────────────────────────────────────────────────────────

export const ProductCatalogTotalsResponseSchema = z.object({
  /** Total products (variant SKUs) matching the filters, across all pages. */
  totalProducts: z.number().int().min(0),
  /** Total deduped price lines surfaced across all matching products. */
  totalPrices: z.number().int().min(0),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const productCatalogContracts = {
  list: oc
    .input(ListProductCatalogInputSchema)
    .output(ListProductCatalogResponseSchema)
    .route({
      method: "GET",
      summary:
        "List product catalog rows (paginated) with price lists for the current organization",
    }),
  classifications: oc
    .output(ProductCatalogClassificationsResponseSchema)
    .route({
      method: "GET",
      summary:
        "Distinct PRODVIEW (family, group, model) triples for the current org's products",
    }),
  totals: oc
    .input(ProductCatalogFilterSchema)
    .output(ProductCatalogTotalsResponseSchema)
    .route({
      method: "GET",
      summary:
        "Total product + price counts for the filtered catalog (all pages)",
    }),
  // Unbounded (the whole filtered catalog, no row cap), so it resolves to a
  // byte stream rather than a materialized value — see `ExportFileStreamSchema`.
  // GET so the client can trigger it as a browser navigation and let the
  // download stream to disk.
  exportExcel: oc
    .input(ProductCatalogFilterSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary: "Stream the full filtered product catalog as an .xlsx download",
    }),
} as const;

// ─── Type exports ───────────────────────────────────────────────────────────

export type ProductCatalogPrice = z.infer<typeof ProductCatalogPriceSchema>;
export type ProductCatalogRow = z.infer<typeof ProductCatalogRowSchema>;
export type ProductCatalogStatus = z.infer<typeof ProductCatalogStatusSchema>;
export type ProductCatalogFilter = z.infer<typeof ProductCatalogFilterSchema>;
export type ListProductCatalogInput = z.infer<
  typeof ListProductCatalogInputSchema
>;
export type ListProductCatalogResponse = z.infer<
  typeof ListProductCatalogResponseSchema
>;
export type ProductCatalogClassification = z.infer<
  typeof ProductCatalogClassificationSchema
>;
export type ProductCatalogClassificationsResponse = z.infer<
  typeof ProductCatalogClassificationsResponseSchema
>;
export type ProductCatalogTotalsResponse = z.infer<
  typeof ProductCatalogTotalsResponseSchema
>;
